import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import { Box, Container, Link, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface LinkItem {
  name: string;
  path: string;
}

const SHOW_GALLERY = false;

const socialLinks = [
  {
    icon: <FacebookIcon aria-label="Facebook" sx={{ fontSize: 20 }} />,
    url: "https://www.facebook.com/share/1BH5WrTomp/?mibextid=LQQJ4d/",
  },
  {
    icon: <InstagramIcon aria-label="Instagram" sx={{ fontSize: 20 }} />,
    url: "https://www.instagram.com/vistafusionstudio/",
  },
  {
    icon: <LinkedInIcon aria-label="LinkedIn" sx={{ fontSize: 20 }} />,
    url: "https://www.linkedin.com/",
  },
];

const pageLinks: LinkItem[] = [
  { name: "Home", path: "#home-section" },
  { name: "Services", path: "#service-section" },
  { name: "About Us", path: "#about-us-section" },
  { name: "Our Process", path: "#our-process-section" },
  ...(SHOW_GALLERY ? [{ name: "Gallery", path: "#gallery-section" }] : []),
  { name: "Contact Us", path: "#contact-us-section" },
];

const serviceLinks: LinkItem[] = [
  {
    name: "Residential Interiors",
    path: SHOW_GALLERY ? "#gallery" : "#service-section",
  },
  {
    name: "Commercial Spaces",
    path: SHOW_GALLERY ? "#gallery" : "#service-section",
  },
  { name: "Retail Design", path: SHOW_GALLERY ? "#gallery" : "#service-section" },
  {
    name: "Hospitality Projects",
    path: SHOW_GALLERY ? "#gallery" : "#service-section",
  },
];

const footerBlockSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "center", md: "flex-start" },
  textAlign: { xs: "center", md: "left" },
  height: "100%",
} as const;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const handleLinkClick = (path: string) => {
    if (SHOW_GALLERY && path === "#gallery") {
      navigate("/gallery");
      return;
    }

    const section = document.querySelector(path);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        mt: { xs: 4.5, md: 5.8 },
        background:
          "linear-gradient(140deg, #2c171b 0%, #3c2025 46%, #4e272e 100%)",
        color: "rgba(255,255,255,0.88)",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 4.2, md: 5.6 },
          px: { xs: 2.2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "1.3fr 1fr 1fr 1.4fr",
            },
            gap: { xs: 2.4, sm: 2.8, md: 3, xl: 2.8 },
          }}
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.56, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <Box sx={footerBlockSx}>
            <Typography
              sx={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: { xs: "1.95rem", md: "2.2rem" },
                fontWeight: 700,
                lineHeight: 1.05,
              }}
            >
              Vista Fusion Studio
            </Typography>
            <Typography
              sx={{
                mt: 1.1,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.9rem",
                lineHeight: 1.72,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "32ch",
                textWrap: "pretty",
              }}
            >
              Bespoke interiors designed for everyday comfort, strong aesthetics,
              and reliable project execution from concept to completion.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1.7,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {socialLinks.map((social) => (
                <Link
                  key={social.url}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.35)",
                    color: "rgba(255,255,255,0.9)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 220ms ease, background-color 220ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      backgroundColor: "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  {social.icon}
                </Link>
              ))}
            </Box>
            </Box>
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.56, delay: 0.08, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <Box sx={footerBlockSx}>
            <Typography
              sx={{
                fontFamily: '"Manrope", sans-serif',
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.74rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.82)",
                mb: 1.1,
              }}
            >
              Pages
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.86,
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              {pageLinks.map((link) => (
                <Link
                  key={link.name}
                  component="button"
                  onClick={() => handleLinkClick(link.path)}
                  underline="none"
                  sx={{
                    width: { xs: "100%", md: "fit-content" },
                    color: "rgba(255,255,255,0.82)",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    textAlign: { xs: "center", md: "left" },
                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Box>
            </Box>
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.56, delay: 0.14, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <Box sx={footerBlockSx}>
            <Typography
              sx={{
                fontFamily: '"Manrope", sans-serif',
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.74rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.82)",
                mb: 1.1,
              }}
            >
              Services
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.86,
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              {serviceLinks.map((link) => (
                <Link
                  key={link.name}
                  component="button"
                  onClick={() => handleLinkClick(link.path)}
                  underline="none"
                  sx={{
                    width: { xs: "100%", md: "fit-content" },
                    color: "rgba(255,255,255,0.82)",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    textAlign: { xs: "center", md: "left" },
                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Box>
            </Box>
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={
              prefersReducedMotion
                ? undefined
                : { duration: 0.56, delay: 0.2, ease: [0.22, 1, 0.36, 1] }
            }
          >
            <Box sx={footerBlockSx}>
            <Typography
              sx={{
                fontFamily: '"Manrope", sans-serif',
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.74rem",
                fontWeight: 700,
                color: "rgba(255,255,255,0.82)",
                mb: 1.1,
              }}
            >
              Contact
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.9rem",
                color: "rgba(255,255,255,0.76)",
                lineHeight: 1.72,
                maxWidth: "34ch",
                overflowWrap: "anywhere",
              }}
            >
              Villa - 437 Praneeth Pranav Knightwoods, Beeramguda, Hyderabad,
              Telangana 502032
            </Typography>
            <Link
              href="mailto:contact@vistafusionstudio.com"
              underline="none"
              sx={{
                display: "block",
                mt: 1.1,
                color: "rgba(255,255,255,0.9)",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.9rem",
                overflowWrap: "anywhere",
              }}
            >
              contact@vistafusionstudio.com
            </Link>
            <Typography
              sx={{
                mt: 0.8,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.9rem",
                color: "rgba(255,255,255,0.84)",
              }}
            >
              +91 90303 56464
            </Typography>
            </Box>
          </motion.div>
        </Box>

        <Box
          sx={{
            mt: { xs: 3.2, md: 3.8 },
            pt: 2.2,
            borderTop: "1px solid rgba(255,255,255,0.16)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.1,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Manrope", sans-serif',
              color: "rgba(255,255,255,0.68)",
              fontSize: "0.79rem",
              maxWidth: "46ch",
            }}
          >
            © {new Date().getFullYear()} Vista Fusion Studio. All rights reserved.
          </Typography>
          <Link
            component="button"
            onClick={() => navigate("/privacy-policy")}
            underline="none"
            sx={{
              fontFamily: '"Manrope", sans-serif',
              color: "rgba(255,255,255,0.84)",
              fontSize: "0.79rem",
              cursor: "pointer",
              textAlign: "center",
              "&:hover": {
                color: "#fff",
              },
            }}
          >
            Privacy Policy & Terms
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
